import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowRight, Check } from "lucide-react";

const CONFIG = {
  "business-name-generator": {
    kw: "Business Name Generator",
    h1: "AI Business Name Generator",
    lead: "Generate brandable business names in seconds. Describe your business and Namable's AI creates memorable names, checks .com availability and estimates domain value.",
    placeholder: "A neighbourhood coffee roastery and café…",
    examples: ["NOVARA", "BREWMOTIVE", "CORVELO", "APEXLY"],
    faqs: [
      ["How does the business name generator work?", "Describe your business in a sentence. Our AI generates dozens of brandable names, scores each for brand strength and checks domain availability instantly."],
      ["Are the names available as domains?", "Yes — every name is checked live against real domain registries so you know what's actually available to register."],
      ["Is it free?", "Your first scan is free with no account required. Paid plans unlock .com search, more scans and estimated domain values."],
    ],
  },
  "brand-name-generator": {
    kw: "Brand Name Generator",
    h1: "AI Brand Name Generator",
    lead: "Craft a brand name people remember. Namable's AI blends creativity and commercial insight to generate distinctive, ownable brand names with available domains.",
    placeholder: "A premium skincare brand for sensitive skin…",
    examples: ["LUMEA", "VERANA", "KORVA", "NIVELLE"],
    faqs: [
      ["What makes a good brand name?", "Great brand names are short, memorable, easy to pronounce and ownable as a domain. Namable scores every suggestion on exactly these factors."],
      ["Can I check the domain too?", "Absolutely — availability is checked live and available .com names show an estimated value."],
      ["Do I need an account?", "No. Try your first scan free, then create an account to save your favourites."],
    ],
  },
  "app-name-generator": {
    kw: "App Name Generator",
    h1: "AI App Name Generator",
    lead: "Find a catchy, app-store-ready name for your product. Describe your app and get short, brandable names with domains you can actually claim.",
    placeholder: "A habit-tracking app with friendly reminders…",
    examples: ["VYNTRO", "TROTLY", "PULSELY", "NEXA"],
    faqs: [
      ["Will the name work on the App Store?", "Namable favours short, distinctive names that stand out in app stores and are easy to search for."],
      ["Can I get a matching domain?", "Yes — each name shows live domain availability so your app and website can share a brand."],
      ["Is the first scan really free?", "Yes, no signup needed for your first scan."],
    ],
  },
  "startup-name-generator": {
    kw: "Startup Name Generator",
    h1: "AI Startup Name Generator",
    lead: "Name your startup like a founder who's thinking big. Namable generates modern, investable startup names with available .com domains and value estimates.",
    placeholder: "A fintech startup for freelancer invoicing…",
    examples: ["NEXORA", "FINVELO", "APEXA", "ZENTIQ"],
    faqs: [
      ["Why does the .com matter for startups?", "A matching .com signals credibility to customers and investors. Namable highlights which .com domains are available and their estimated value."],
      ["How many names do I get?", "Each scan returns dozens of ranked suggestions you can sort and shortlist."],
      ["Can I save names for my team?", "Create a free account to save and compare your favourites."],
    ],
  },
  "company-name-generator": {
    kw: "Company Name Generator",
    h1: "AI Company Name Generator",
    lead: "Generate a professional company name that's ready to register. Namable creates credible, brandable company names with live domain availability.",
    placeholder: "A logistics company for last-mile delivery…",
    examples: ["CORVEX", "VANTALY", "APEXCORE", "NOVELA"],
    faqs: [
      ["Are these names trademark-checked?", "Namable provides an AI trademark-risk indicator on each name's detail view as a helpful starting point — always confirm with a professional search."],
      ["Can I check availability for my country?", "Set your country in your profile to unlock local extensions like .com.au or .co.uk."],
      ["Is there a free option?", "Yes — your first scan is free."],
    ],
  },
  "domain-name-generator": {
    kw: "Domain Name Generator",
    h1: "AI Domain Name Generator & Value Finder",
    lead: "Discover available domains worth owning. Namable generates brandable names, checks .com availability live and estimates each domain's value — perfect for founders and domain investors.",
    placeholder: "A brandable .com I could register and build on…",
    examples: ["KORVA.com", "VYNTRO.com", "NOVARA.com", "APEXLY.com"],
    faqs: [
      ["How accurate are the domain values?", "Values are AI estimates for guidance, based on length, brandability and patterns — not formal appraisals."],
      ["Can I find domains to resell?", "Yes — Namable surfaces available .com domains with estimated value, useful for identifying names worth holding."],
      ["Do you register the domain for me?", "Namable helps you find and evaluate names; you register through your preferred registrar."],
    ],
  },
};

export default function SeoLanding({ slug }) {
  const cfg = CONFIG[slug];
  const navigate = useNavigate();
  const { track } = useAuth();
  const [idea, setIdea] = useState("");

  useEffect(() => {
    track("page_viewed", { page: slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!cfg) return null;

  const go = () => navigate(idea.trim() ? `/generate?desc=${encodeURIComponent(idea.trim())}` : "/generate");

  return (
    <div>
      <Seo title={`${cfg.h1} — Free & Instant | Namable`} description={cfg.lead} path={`/${slug}`} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-[760px] mx-auto px-4 sm:px-6 pt-16 pb-12 sm:pt-20">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface border hairline px-3 py-1 text-[12px] font-semibold text-ink-muted shadow-soft">
            <Sparkles size={13} className="text-brand" /> {cfg.kw}
          </span>
          <h1 className="font-display text-[34px] sm:text-[48px] leading-[1.03] font-extrabold tracking-[-0.03em] text-ink mt-5">
            {cfg.h1}
          </h1>
          <p className="text-[16px] sm:text-[17px] text-ink-muted mt-4 leading-relaxed max-w-[600px]">{cfg.lead}</p>

          <div className="mt-7 card-soft p-2 flex flex-col sm:flex-row gap-2 max-w-[560px]">
            <input
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && go()}
              placeholder={cfg.placeholder}
              data-testid="seo-idea-input"
              className="flex-1 bg-transparent px-3.5 py-3 text-[15px] text-ink placeholder:text-ink-subtle outline-none"
            />
            <Button onClick={go} data-testid="seo-cta-btn" className="h-12 px-6 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow whitespace-nowrap">
              Generate names <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            {cfg.examples.map((e) => (
              <span key={e} className="font-display font-extrabold text-[14px] tracking-[-0.01em] text-ink bg-surface border hairline rounded-full px-3.5 py-1.5">
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* content for SEO */}
      <section className="max-w-[760px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {["Describe your idea", "Get ranked names", "Claim the domain"].map((t, i) => (
            <div key={t} className="card-soft p-5">
              <p className="overline text-brand">Step {i + 1}</p>
              <p className="text-[15px] font-bold text-ink mt-1.5">{t}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display text-[26px] font-extrabold tracking-[-0.02em] text-ink mt-14">Frequently asked questions</h2>
        <div className="mt-5 space-y-3">
          {cfg.faqs.map(([q, a]) => (
            <div key={q} className="card-soft p-5">
              <p className="text-[15px] font-bold text-ink flex items-start gap-2">
                <Check size={16} className="text-brand mt-1 shrink-0" /> {q}
              </p>
              <p className="text-[14px] text-ink-muted leading-relaxed mt-2 pl-6">{a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xxl brand-gradient p-10 text-center text-white shadow-glow">
          <h2 className="font-display text-[26px] font-extrabold">Ready to find your name?</h2>
          <Button onClick={go} className="mt-5 h-12 px-7 rounded-xl bg-white text-brand hover:bg-white/90 font-extrabold" data-testid="seo-bottom-cta">
            Try Namable free
          </Button>
        </div>
      </section>
    </div>
  );
}
